import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useFormik } from "lib/formik"
import * as Yup from "utils/yup"
import Breadcrumbs from "components/Common/Breadcrumb"
import { PermissionTile } from "components/Roles/RolePermissionsSelector"
import DeliveryPartnerPermissionsEditor from "components/Roles/DeliveryPartnerPermissionsEditor"
import { isDeliveryPartnerCategory } from "utils/deliveryPartnerPermissionLayout"
import "components/Roles/role-permissions.scss"
import {
  saCreatePlan,
  saGetIntegrationsCatalogTree,
  saGetPlanDetails,
  saUpdatePlan,
} from "store/actions/superAdminAction"
import { clearPlanDetails } from "store/reducers/superAdminReducer"
import { showAlert } from "store/reducers/alertReducer"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import isPermissionEnabled from "common/isPermissionEnabled"
import { SYSTEM_MODULES } from "constants/modules"
import { normalizePlanFeatureKey, countPlanModules, planFeaturesToSelectedModules, sanitizeSelectedPlanModules } from "constants/planPermissionMap"
import {
  buildSelectionsForCategories,
  buildSelectionsFromGrantKeys,
  getCategoryIdsForModule,
  getPermissionGroupByCategoryId,
  getPermissionLabel,
  inferGrantKeysFromPlan,
  isTenantCountryMutationPermission,
  isLegacyPlanBrandPermissionCategory,
  mergeCategorySelectionWithCatalog,
  permissionGrantsFromSelections,
  syncSelectionsForModule,
  toggleCategoryAllInSelection,
} from "utils/planPermissionGrants"
import {
  applyPlanApiError,
  buildPlanDescriptionSchema,
  buildPlanNameSchema,
  buildPlanNumericLimitSchema,
  isPlanActionSuccess,
} from "utils/planValidation"
import { NUMERIC_SAFE_MAX } from "utils/numericValidation"

const CURRENCIES = ["SAR", "USD", "AED", "KWD", "BHD", "OMR", "QAR"]
const PLAN_TYPES = [
  { value: "normal", labelKey: "Normal" },
  { value: "enterprise", labelKey: "Enterprise" },
]
/* Not wired to plan API yet — re-enable with Add-ons & Module Limits UI below.
const EMPTY_ADDON = { label: "", description: "", price: "", unit: "" }
const EMPTY_LIMIT = { moduleId: "", limitKey: "", limitValue: "" }
*/

const buildDefaultCategoryIds = () => [
  ...new Set(
    SYSTEM_MODULES.filter(module => module.defaultOn).flatMap(module =>
      getCategoryIdsForModule(module.id)
    )
  ),
]

/** Native checkbox row — works outside reactstrap <Form> (integrations use the same pattern). */
const NativeCheckbox = ({ id, checked, onActivate, className, inputStyle, children }) => (
  <label
    htmlFor={id}
    className={className}
    style={{ cursor: "pointer", userSelect: "none", marginBottom: 0 }}
    onClick={e => {
      e.preventDefault()
      e.stopPropagation()
      onActivate()
    }}
  >
    <input
      id={id}
      type="checkbox"
      readOnly
      checked={!!checked}
      tabIndex={-1}
      style={{
        width: 16,
        height: 16,
        flexShrink: 0,
        accentColor: "var(--bs-primary, #556ee6)",
        pointerEvents: "none",
        ...inputStyle,
      }}
    />
    {children}
  </label>
)

/** Same toggle logic as tenant/super-admin role permission screens. */
const applyPlanPermissionToggle = (selection, permissionId, subPermissionId = 0) => {
  if (!selection) return selection
  return {
    ...selection,
    permissions: selection.permissions.map(permission => {
      if (String(permission.id) !== String(permissionId)) return permission
      if (!subPermissionId) {
        const newStatus = !permission.isEnabled
        return {
          ...permission,
          isEnabled: newStatus,
          subPermissions: (permission.subPermissions || []).map(sub => ({
            ...sub,
            isEnabled: newStatus,
          })),
        }
      }
      const subPermissions = (permission.subPermissions || []).map(sub =>
        String(sub.id) === String(subPermissionId)
          ? { ...sub, isEnabled: !sub.isEnabled }
          : sub
      )
      return { ...permission, isEnabled: permission.isEnabled, subPermissions }
    }),
  }
}

const ModuleCategoryPermissions = ({
  categoryId,
  selection,
  uiLang,
  t,
  onPermissionChange,
  onToggleCategoryAll,
}) => {
  const group = getPermissionGroupByCategoryId(categoryId)
  if (!group) return null

  const mergedSelection = useMemo(
    () => mergeCategorySelectionWithCatalog(categoryId, selection),
    [categoryId, selection]
  )
  const stateByPermissionId = useMemo(() => {
    const map = new Map()
    ;(mergedSelection?.permissions || []).forEach(permission => {
      map.set(String(permission.id), permission)
    })
    return map
  }, [mergedSelection])

  const groupTitle =
    uiLang === "ar" || uiLang === "sa"
      ? group.titleAr || group.title
      : group.title || group.titleAr

  const mergedPermissions = useMemo(() => {
    return (group.permissions || []).map(permission => {
      const statePerm = stateByPermissionId.get(String(permission.id))
      if (!statePerm) {
        return permission
      }

      return {
        ...permission,
        isEnabled: statePerm.isEnabled ?? false,
        subPermissions: (permission.subPermissions || []).map(sub => {
          const stateSub = statePerm.subPermissions?.find(
            item => String(item.id) === String(sub.id)
          )
          return {
            ...sub,
            isEnabled: stateSub?.isEnabled ?? false,
          }
        }),
      }
    })
  }, [group.permissions, stateByPermissionId])

  // Country add/edit/delete are platform-only — hide from plan permission checkboxes.
  // Legacy FNB/B2C brand toggles — use Assigned Brands section instead.
  const visiblePermissions = (group.permissions || []).filter(
    permission =>
      !isTenantCountryMutationPermission(categoryId, permission.id)
  )

  if (isLegacyPlanBrandPermissionCategory(categoryId)) {
    return null
  }

  const allParentsSelected = visiblePermissions.every(permission => {
    const statePerm = stateByPermissionId.get(String(permission.id))
    return statePerm?.isEnabled ?? false
  })

  if (isDeliveryPartnerCategory(categoryId)) {
    return (
      <div className="plan-module-permissions mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="small fw-semibold">{groupTitle}</span>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              onToggleCategoryAll(!allParentsSelected)
            }}
          >
            {allParentsSelected ? t("Deselect all") : t("Select all")}
          </button>
        </div>
        <DeliveryPartnerPermissionsEditor
          permissions={mergedPermissions}
          language={uiLang}
          t={t}
          getPermissionLabel={(permission, lang) => getPermissionLabel(permission, lang)}
          onPermissionChange={(permissionId, subPermissionId) =>
            onPermissionChange(permissionId, subPermissionId)
          }
        />
      </div>
    )
  }

  return (
    <div className="plan-module-permissions mb-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="small fw-semibold">{groupTitle}</span>
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            onToggleCategoryAll(!allParentsSelected)
          }}
        >
          {allParentsSelected ? t("Deselect all") : t("Select all")}
        </button>
      </div>
      <ul className="role-permissions-split__perm-list mb-0 ps-1">
        {visiblePermissions.map(permission => {
          const statePerm = stateByPermissionId.get(String(permission.id))
          const parentChecked = statePerm?.isEnabled ?? false
          return (
            <li key={permission.id} className="role-permissions-split__perm-item-wrap mb-2">
              <PermissionTile
                label={getPermissionLabel(permission, uiLang)}
                checked={parentChecked}
                onChange={() => onPermissionChange(permission.id)}
              />
              {parentChecked && (permission.subPermissions || []).length > 0 && (
                <ul className="role-permissions-split__perm-list ms-2 mt-1 mb-0">
                  {permission.subPermissions.map(sub => {
                    const stateSub = statePerm?.subPermissions?.find(
                      item => String(item.id) === String(sub.id)
                    )
                    return (
                      <li
                        key={sub.id}
                        className="role-permissions-split__perm-sub-wrap mb-1"
                      >
                        <PermissionTile
                          label={getPermissionLabel(sub, uiLang)}
                          checked={stateSub?.isEnabled ?? false}
                          isSub
                          onChange={() => onPermissionChange(permission.id, sub.id)}
                        />
                      </li>
                    )
                  })}
                </ul>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const asIdString = v => {
  if (v == null || v === "") return ""
  if (typeof v === "string" || typeof v === "number") return String(v)
  if (typeof v === "object") {
    if (typeof v.$oid === "string") return v.$oid
    if (v._id != null) return asIdString(v._id)
    if (v.id != null && typeof v.id !== "object") return String(v.id)
    if (typeof v.toString === "function") {
      const s = v.toString()
      if (s && s !== "[object Object]" && !String(s).startsWith("[object ")) return s
    }
  }
  return ""
}

/** Catalog APIs sometimes return a map instead of an array. */
const asChildArray = raw => {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === "object") return Object.values(raw).filter(Boolean)
  return []
}

const inferNodeId = node => {
  if (node == null || node === "") return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (typeof node !== "object") return ""
  const intg = node.integration
  if (intg != null) {
    if (typeof intg === "string" || typeof intg === "number") return String(intg)
    if (typeof intg === "object") {
      const nested =
        asIdString(intg._id) ||
        asIdString(intg.id) ||
        asIdString(intg.integrationId)
      if (nested) return nested
    }
  }
  return (
    asIdString(node._id) ||
    asIdString(node.id) ||
    asIdString(node.integrationId) ||
    (typeof node.key === "string" ? node.key : "") ||
    asIdString(node.code) ||
    ""
  )
}

const inferChildren = parent => {
  if (!parent || typeof parent !== "object") return []
  const c =
    parent.children ||
    parent.subIntegrations ||
    parent.childIntegrations ||
    parent.integrations ||
    parent.integrationList ||
    parent.items ||
    parent.subs ||
    parent.nodes ||
    parent.leaves ||
    parent.subCategories ||
    parent.subItems ||
    parent.integrationChildren
  return asChildArray(c)
}

const inferDisplayName = (node, lang) => {
  if (!node) return ""
  const title = node.title
  if (title && typeof title === "object") {
    const preferAr = lang === "ar" || lang === "sa"
    return (
      (preferAr ? title.ar : title.en) ||
      title.en ||
      title.ar ||
      ""
    )
  }
  if (typeof title === "string" && title) return title
  if (node.name) return String(node.name)
  if (node.code) return String(node.code)
  return inferNodeId(node) || "—"
}

const integrationIdFromPlanEntry = x => {
  if (x == null) return ""
  if (typeof x === "string" || typeof x === "number") return String(x)
  if (typeof x === "object") {
    if (typeof x.integration === "string" || typeof x.integration === "number") {
      return String(x.integration)
    }
    if (x.integration && typeof x.integration === "object") {
      const nested =
        asIdString(x.integration._id) ||
        asIdString(x.integration.id) ||
        asIdString(x.integration.integrationId)
      if (nested) return nested
    }
    return (
      asIdString(x._id) ||
      asIdString(x.id) ||
      asIdString(x.integrationId) ||
      ""
    )
  }
  return ""
}

const AddEditPlan = ({ mode }) => {
  const { t, i18n } = useTranslation()
  const isView = mode === "view"
  const isEdit = mode === "edit"
  const isDetailsMode = isEdit || isView
  const uiLang = i18n.language || "en"
  document.title = isView
    ? t("View Plan | Super Admin")
    : isEdit
      ? t("Edit Plan | Super Admin")
      : t("Create Plan | Super Admin")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const planId = new URLSearchParams(location.search).get("id")

  const {
    plansLoading,
    planActionLoading,
    planDetails,
    integrationCatalogTree,
    integrationCatalogTreeLoading,
  } = useSelector(state => state.SuperAdmin)
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const canCreatePlans = isPermissionEnabled(
    userRole,
    105,
    "sa_plans_2",
    accountType,
    accountScope
  )
  const canEditPlans = isPermissionEnabled(
    userRole,
    105,
    "sa_plans_3",
    accountType,
    accountScope
  )
  const canViewPlans = isPermissionEnabled(
    userRole,
    105,
    "sa_plans_1",
    accountType,
    accountScope
  )

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (isEdit && !canEditPlans) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (isView && !canViewPlans) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (!isDetailsMode && !canCreatePlans) {
      navigateToAccessibleRoute(navigate)
    }
  }, [
    isView,
    canViewPlans,
    isSuperAdminSession,
    isDetailsMode,
    isEdit,
    canCreatePlans,
    canEditPlans,
    navigate,
  ])

  const [selectedModules, setSelectedModules] = useState(
    SYSTEM_MODULES.filter(m => m.defaultOn).map(m => ({
      id: m.id,
      isDefault: true,
    }))
  )
  /* Not wired to plan API yet — re-enable with Add-ons & Module Limits UI below.
  const [addOns, setAddOns] = useState([{ ...EMPTY_ADDON }])
  const [limits, setLimits] = useState([{ ...EMPTY_LIMIT }])
  */
  const [expandedParentKeys, setExpandedParentKeys] = useState([])
  const [expandedModuleIds, setExpandedModuleIds] = useState([])
  const [permissionSelections, setPermissionSelections] = useState(() =>
    isDetailsMode ? {} : buildSelectionsForCategories(buildDefaultCategoryIds(), true)
  )
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState([])
  const [socialLogins, setSocialLogins] = useState({ google: false, apple: false })
  const [modulesError, setModulesError] = useState("")

  const integrationAutoExpandDone = useRef(false)
  const hydratedPlanIdRef = useRef(null)

  const integrationCatalogParents = useMemo(() => {
    const raw = integrationCatalogTree
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === "object") {
      if (Array.isArray(raw.tree)) return raw.tree
      if (Array.isArray(raw.catalog)) return raw.catalog
      if (Array.isArray(raw.list)) return raw.list
      if (Array.isArray(raw.categories)) return raw.categories
    }
    return []
  }, [integrationCatalogTree])

  useEffect(() => {
    hydratedPlanIdRef.current = null
  }, [isDetailsMode, planId])

  useEffect(() => {
    if (integrationCatalogTreeLoading) return
    if (integrationCatalogParents.length === 0) {
      integrationAutoExpandDone.current = false
      return
    }
    if (integrationAutoExpandDone.current) return
    integrationAutoExpandDone.current = true
    setExpandedParentKeys(
      integrationCatalogParents.map((p, i) => String(p._id || i))
    )
  }, [integrationCatalogTreeLoading, integrationCatalogParents])

  useEffect(() => {
    if (isDetailsMode && planId) {
      dispatch(saGetPlanDetails({ id: planId }))
    }
    return () => {
      dispatch(clearPlanDetails())
    }
  }, [dispatch, isDetailsMode, planId])

  useEffect(() => {
    let cancelled = false
    dispatch(saGetIntegrationsCatalogTree({ all: true })).then(action => {
      if (cancelled) return
      if (saGetIntegrationsCatalogTree.rejected.match(action)) {
        dispatch(
          showAlert({
            type: "danger",
            message: t("Could not load integration catalog."),
          })
        )
      }
    })
    return () => {
      cancelled = true
    }
  }, [dispatch, t])

  useEffect(() => {
    if (!isDetailsMode) setSelectedIntegrationIds([])
  }, [isDetailsMode])

  useEffect(() => {
    if (isDetailsMode) return
    setExpandedModuleIds(prev => {
      if (prev.length > 0) return prev
      return SYSTEM_MODULES.filter(module => module.defaultOn).map(module => module.id)
    })
  }, [isDetailsMode])

  useEffect(() => {
    const detailsId = planDetails?._id || planDetails?.id
    if (!isDetailsMode || !planId || !detailsId || String(detailsId) !== String(planId)) {
      return
    }
    if (hydratedPlanIdRef.current === String(planId)) {
      return
    }
    hydratedPlanIdRef.current = String(planId)
    const hydratedModules = planFeaturesToSelectedModules(planDetails)
    const hydratedModuleIds = hydratedModules.map(module => module.id)

    const hydratedCategoryIds = [
      ...new Set(hydratedModuleIds.flatMap(moduleId => getCategoryIdsForModule(moduleId))),
    ]

    setPermissionSelections(
      buildSelectionsFromGrantKeys(
        hydratedCategoryIds,
        inferGrantKeysFromPlan(planDetails)
      )
    )

    if (hydratedModules.length > 0) {
      setSelectedModules(hydratedModules)
      setExpandedModuleIds(hydratedModuleIds)
    }
    /* Not wired to plan API yet.
    if (Array.isArray(planDetails.addOns) && planDetails.addOns.length > 0) {
      setAddOns(planDetails.addOns)
    }
    if (Array.isArray(planDetails.limits) && planDetails.limits.length > 0) {
      setLimits(planDetails.limits)
    }
    */

    const nonEmptyArr = arr => Array.isArray(arr) && arr.length > 0

    const hasIntegrationPayload =
      (Array.isArray(planDetails.features) &&
        planDetails.features.some(
          f =>
            normalizePlanFeatureKey(f?.name) === "integrations" &&
            nonEmptyArr(f.integrationIds)
        )) ||
      nonEmptyArr(planDetails.integrationIds) ||
      nonEmptyArr(planDetails.selectedIntegrationIds) ||
      nonEmptyArr(planDetails.integrations)

    if (!hasIntegrationPayload) {
      setSelectedIntegrationIds([])
      return
    }

    const raw = []
    if (Array.isArray(planDetails.features)) {
      planDetails.features.forEach(f => {
        if (
          normalizePlanFeatureKey(f?.name) === "integrations" &&
          Array.isArray(f.integrationIds)
        ) {
          raw.push(...f.integrationIds)
        }
      })
    }
    if (Array.isArray(planDetails.integrationIds)) {
      raw.push(...planDetails.integrationIds)
    }
    if (Array.isArray(planDetails.selectedIntegrationIds)) {
      raw.push(...planDetails.selectedIntegrationIds)
    }
    if (Array.isArray(planDetails.integrations)) {
      raw.push(...planDetails.integrations)
    }
    const merged = [
      ...new Set(
        raw
          .map(x => {
            if (!x) return ""
            if (typeof x === "string") return x
            if (typeof x === "number") return String(x)
            return String(x._id || x.id || x.integrationId || "")
          })
          .filter(Boolean)
      ),
    ]
    setSelectedIntegrationIds(merged)

    const planSocial = planDetails.socialLogins || {}
    setSocialLogins({
      google: planSocial.google === true,
      apple: planSocial.apple === true,
    })
  }, [isDetailsMode, planId, planDetails])

  const planFormInitialValues = useMemo(
    () => ({
      name: planDetails?.name || "",
      description: planDetails?.description || "",
      billingCycle: planDetails?.billingCycle || "monthly",
      price: planDetails?.price ?? "",
      currency: planDetails?.currency || "SAR",
      maxAdminUsers: Number(planDetails?.maxAdminUsers || 0),
      maxBranches: Number(planDetails?.maxBranches || 0),
      isActive: (planDetails?.status || "active") === "active",
      planType: planDetails?.planType === "enterprise" ? "enterprise" : "normal",
    }),
    [
      planDetails?._id,
      planDetails?.name,
      planDetails?.description,
      planDetails?.billingCycle,
      planDetails?.price,
      planDetails?.currency,
      planDetails?.maxAdminUsers,
      planDetails?.maxBranches,
      planDetails?.status,
      planDetails?.planType,
    ]
  )

  const formik = useFormik({
    enableReinitialize: isDetailsMode,
    initialValues: planFormInitialValues,
    validationSchema: Yup.object({
      name: buildPlanNameSchema(t),
      description: buildPlanDescriptionSchema(t),
      price: Yup.number()
        .typeError(t("Price must be a number"))
        .min(0, t("Price must be 0 or greater"))
        .required(t("Price is required")),
      billingCycle: Yup.string().required(t("Billing cycle is required")),
      currency: Yup.string().required(t("Currency is required")),
      maxAdminUsers: buildPlanNumericLimitSchema(t, t("Max Users")),
      maxBranches: buildPlanNumericLimitSchema(t, t("Max Branches")),
    }),
    onSubmit: values => {
      const validSelectedModules = sanitizeSelectedPlanModules(selectedModules)
      if (validSelectedModules.length === 0) {
        const message = t("At least one module must be selected.")
        setModulesError(message)
        dispatch(showAlert({ type: "error", text: message }))
        return
      }
      setModulesError("")

      const integrationIdList = selectedIntegrationIds.map(String)

      const moduleFeatures = validSelectedModules.map(m => ({
        name: m.id,
        limit: m.isDefault ? "default" : "enabled",
      }))

      // Server stores integrationIds on the plan only; merges with the
      // Integrations feature row for GET. Send top-level integrationIds +
      // { name, limit } only so create/update match API §2.1.
      const features =
        integrationIdList.length > 0
          ? [...moduleFeatures, { name: "integrations", limit: "enabled" }]
          : moduleFeatures

      const payload = {
        name: values.name.trim(),
        description: values.description,
        billingCycle: values.billingCycle,
        price: Number(values.price),
        currency: values.currency,
        maxAdminUsers: Number(values.maxAdminUsers || 0),
        maxBranches: Number(values.maxBranches || 0),
        status: values.isActive ? "active" : "inactive",
        planType: values.planType,
        features,
        basePermissions: [],
        permissionGrants: permissionGrantsFromSelections(permissionSelections),
        integrationIds: integrationIdList,
        socialLogins,
      }
      if (isEdit) {
        payload.id = planId
        dispatch(saUpdatePlan(payload)).then(res => {
          if (isPlanActionSuccess(res)) {
            dispatch(showAlert({ type: "success", message: t("Plan updated successfully") }))
            navigate(ROUTES.SA_PLANS)
            return
          }
          applyPlanApiError(res?.payload, {
            formik,
            dispatch,
            showAlert,
            t,
            onModulesError: setModulesError,
          })
        })
      } else {
        dispatch(saCreatePlan(payload)).then(res => {
          if (isPlanActionSuccess(res)) {
            dispatch(showAlert({ type: "success", message: t("Plan created successfully") }))
            navigate(ROUTES.SA_PLANS)
            return
          }
          applyPlanApiError(res?.payload, {
            formik,
            dispatch,
            showAlert,
            t,
            onModulesError: setModulesError,
          })
        })
      }
    },
  })

  // ── Plan permission helpers (same toggle pattern as role screens) ─────────────
  const handlePlanPermissionChange = (categoryId, permissionId, subPermissionId = 0) => {
    setPermissionSelections(prev => {
      const catId = Number(categoryId)
      const merged = mergeCategorySelectionWithCatalog(catId, prev[catId])
      if (!merged) return prev
      return {
        ...prev,
        [catId]: applyPlanPermissionToggle(merged, permissionId, subPermissionId),
      }
    })
  }

  const handlePlanCategorySelectAll = (categoryId, selectAll) => {
    setPermissionSelections(prev => {
      const catId = Number(categoryId)
      const merged = mergeCategorySelectionWithCatalog(catId, prev[catId])
      if (!merged) return prev
      return {
        ...prev,
        [catId]: toggleCategoryAllInSelection(merged, selectAll),
      }
    })
  }

  const syncModulePermissionSelections = (moduleId, enabled) => {
    setPermissionSelections(prev => syncSelectionsForModule(prev, moduleId, enabled))
  }

  // ── Module helpers ──────────────────────────────────────────────────────────
  const isModuleSelected = id => selectedModules.some(m => m.id === id)
  const isModuleDefault = id => selectedModules.some(m => m.id === id && m.isDefault)

  const toggleModule = id => {
    if (isModuleSelected(id)) {
      setSelectedModules(prev =>
        sanitizeSelectedPlanModules(prev.filter(m => m.id !== id))
      )
      syncModulePermissionSelections(id, false)
      setExpandedModuleIds(prev => prev.filter(moduleId => moduleId !== id))
    } else {
      setModulesError("")
      setSelectedModules(prev =>
        sanitizeSelectedPlanModules([...prev, { id, isDefault: false }])
      )
      syncModulePermissionSelections(id, true)
      setExpandedModuleIds(prev => (prev.includes(id) ? prev : [...prev, id]))
    }
  }

  const setModuleSelected = (id, isChecked) => {
    if (isChecked) {
      setModulesError("")
      setSelectedModules(prev =>
        sanitizeSelectedPlanModules(
          prev.some(m => m.id === id) ? prev : [...prev, { id, isDefault: false }]
        )
      )
      syncModulePermissionSelections(id, true)
      setExpandedModuleIds(prev => (prev.includes(id) ? prev : [...prev, id]))
      return
    }
    setSelectedModules(prev =>
      sanitizeSelectedPlanModules(prev.filter(m => m.id !== id))
    )
    syncModulePermissionSelections(id, false)
    setExpandedModuleIds(prev => prev.filter(moduleId => moduleId !== id))
  }

  const setModuleDefaultEnabled = (id, isDefault) => {
    setSelectedModules(prev =>
      prev.map(m => (m.id === id ? { ...m, isDefault } : m))
    )
  }

  const validSelectedModules = useMemo(
    () => sanitizeSelectedPlanModules(selectedModules),
    [selectedModules]
  )

  const allModulesSelected = SYSTEM_MODULES.every(mod => isModuleSelected(mod.id))

const buildAllModuleCategoryIds = () =>
  [
    ...new Set(
      SYSTEM_MODULES.flatMap(mod => getCategoryIdsForModule(mod.id))
    ),
  ]

  const handleSelectAllModules = () => {
    if (allModulesSelected) {
      setSelectedModules([])
      setModulesError("")
      setPermissionSelections({})
      setExpandedModuleIds([])
      return
    }
    setSelectedModules(
      SYSTEM_MODULES.map(mod => ({
        id: mod.id,
        isDefault: !!mod.defaultOn,
      }))
    )
    setModulesError("")
    setPermissionSelections(
      buildSelectionsForCategories(buildAllModuleCategoryIds(), true)
    )
    setExpandedModuleIds(SYSTEM_MODULES.map(mod => mod.id))
  }

  const toggleModuleExpand = (moduleId, event) => {
    event?.stopPropagation()
    if (!isModuleSelected(moduleId)) return
    setExpandedModuleIds(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    )
  }

  const toggleModuleDefault = id => {
    setSelectedModules(prev =>
      prev.map(m => (m.id === id ? { ...m, isDefault: !m.isDefault } : m))
    )
  }

  /* Not wired to plan API yet — re-enable with Add-ons & Module Limits UI below.
  const updateAddOn = (idx, field, value) => {
    setAddOns(prev => prev.map((a, i) => (i === idx ? { ...a, [field]: value } : a)))
  }
  const addAddOnRow = () => setAddOns(prev => [...prev, { ...EMPTY_ADDON }])
  const removeAddOnRow = idx => setAddOns(prev => prev.filter((_, i) => i !== idx))

  const updateLimit = (idx, field, value) => {
    setLimits(prev => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)))
  }
  const addLimitRow = () => setLimits(prev => [...prev, { ...EMPTY_LIMIT }])
  const removeLimitRow = idx => setLimits(prev => prev.filter((_, i) => i !== idx))
  */

  if (isDetailsMode && plansLoading && !planDetails?._id && !planDetails?.id) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <h4 className="mb-3">
            {isView
              ? t("View Plan")
              : isEdit
                ? t("Edit Plan")
                : t("Create Subscription Plan")}
          </h4>

          {isView && (
            <div className="alert alert-info py-2">
              {t("Read-only mode: plan configuration is shown for review only.")}
            </div>
          )}

          <div style={{ pointerEvents: isView ? "none" : "auto" }}>
          <Form id="sa-plan-form" onSubmit={isView ? undefined : formik.handleSubmit}>
            {/* ── Basic Information ─────────────────────────────────────────── */}
            <Card>
              <CardBody>
                <h5 className="card-title mb-3">{t("Basic Information")}</h5>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Plan Name")} *</Label>
                      <Input
                        name="name"
                        placeholder={t("e.g. Starter, Growth, Enterprise")}
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.name && !!formik.errors.name}
                      />
                      <FormFeedback>{formik.errors.name}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Billing Cycle")} *</Label>
                      <Input
                        type="select"
                        name="billingCycle"
                        value={formik.values.billingCycle}
                        onChange={formik.handleChange}
                        invalid={formik.touched.billingCycle && !!formik.errors.billingCycle}
                      >
                        <option value="monthly">{t("Monthly")}</option>
                        <option value="annual">{t("Annual")}</option>
                      </Input>
                      <FormFeedback>{formik.errors.billingCycle}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label className="d-block">{t("Plan type")}</Label>
                      <div className="d-flex flex-wrap gap-3">
                        {PLAN_TYPES.map(({ value, labelKey }) => (
                          <div
                            key={value}
                            className="form-check mb-0"
                            style={{ cursor: "pointer" }}
                            onClick={() => formik.setFieldValue("planType", value)}
                          >
                            <input
                              className="form-check-input"
                              type="radio"
                              id={`planType_${value}`}
                              name="planType"
                              value={value}
                              checked={formik.values.planType === value}
                              onChange={() => formik.setFieldValue("planType", value)}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`planType_${value}`}
                              style={{ cursor: "pointer" }}
                            >
                              {t(labelKey)}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
                        {formik.values.planType === "enterprise"
                          ? t("Enterprise plans are only assignable to specific tenants by super admin.")
                          : t("Normal plans appear in tenant onboarding and self-service billing.")}
                      </div>
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label>{t("Description")}</Label>
                      <Input
                        type="textarea"
                        name="description"
                        rows={3}
                        placeholder={t("Short description shown on plan cards")}
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.description &&
                          !!formik.errors.description
                        }
                      />
                      <FormFeedback>{formik.errors.description}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>{t("Base Price")} *</Label>
                      <Input
                        name="price"
                        type="number"
                        min={0}
                        max={NUMERIC_SAFE_MAX}
                        step="0.01"
                        placeholder="0.00"
                        value={formik.values.price}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.price && !!formik.errors.price}
                      />
                      <FormFeedback>{formik.errors.price}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>{t("Currency")} *</Label>
                      <Input
                        type="select"
                        name="currency"
                        value={formik.values.currency}
                        onChange={formik.handleChange}
                      >
                        {CURRENCIES.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>{t("Max Users")}</Label>
                      <Input
                        name="maxAdminUsers"
                        type="number"
                        min={0}
                        max={NUMERIC_SAFE_MAX}
                        placeholder="0"
                        value={formik.values.maxAdminUsers}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.maxAdminUsers && !!formik.errors.maxAdminUsers
                        }
                      />
                      <FormFeedback>{formik.errors.maxAdminUsers}</FormFeedback>
                      <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
                        {t("0 means unlimited")}
                      </div>
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>{t("Max Branches")}</Label>
                      <Input
                        name="maxBranches"
                        type="number"
                        min={0}
                        max={NUMERIC_SAFE_MAX}
                        placeholder="0"
                        value={formik.values.maxBranches}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.maxBranches && !!formik.errors.maxBranches
                        }
                      />
                      <FormFeedback>{formik.errors.maxBranches}</FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md={4} className="d-flex align-items-end pb-3">
                    <NativeCheckbox
                      id="isActive"
                      checked={formik.values.isActive}
                      className="d-inline-flex align-items-center gap-2 mb-0"
                      onActivate={() =>
                        formik.setFieldValue("isActive", !formik.values.isActive)
                      }
                    >
                      <span>{t("Active")}</span>
                    </NativeCheckbox>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Form>

            {/* ── Module Selector (outside <Form> so permission clicks are reliable) ── */}
            <Card>
              <CardBody>
                <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                  <div>
                    <h5 className="card-title mb-1">{t("Modules")}</h5>
                    <p className="text-muted small mb-0">
                      {t(
                        "Check a module to include it in this plan, then expand it to choose which screens and actions tenants may use. Mark \"Enabled by default\" to turn the module on automatically for new tenants."
                      )}
                    </p>
                  </div>
                  <NativeCheckbox
                    id="select_all_modules"
                    checked={allModulesSelected}
                    className="d-inline-flex align-items-center gap-2 flex-shrink-0 mt-1"
                    onActivate={handleSelectAllModules}
                  >
                    <span className="fw-medium text-nowrap">
                      {allModulesSelected ? t("Deselect") : t("Select All")}{" "}
                      {t("Modules")}
                    </span>
                  </NativeCheckbox>
                </div>
                <Row className="g-2">
                  {SYSTEM_MODULES.map(mod => {
                    const selected = isModuleSelected(mod.id)
                    const isDefault = isModuleDefault(mod.id)
                    const isExpanded = expandedModuleIds.includes(mod.id)
                    const moduleCategoryIds = getCategoryIdsForModule(mod.id)
                    return (
                      <Col key={mod.id} xl={6} lg={12} md={12}>
                        <div
                          className={`border rounded p-3 h-100 ${selected ? "border-primary bg-light" : ""}`}
                        >
                          <div className="d-flex align-items-start gap-2">
                            <NativeCheckbox
                              id={`mod_${mod.id}`}
                              checked={selected}
                              className="d-inline-flex mt-1 flex-shrink-0"
                              onActivate={() => setModuleSelected(mod.id, !selected)}
                            />
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <span
                                  className="mb-0 fw-medium"
                                  style={{ cursor: "pointer" }}
                                  onClick={e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setModuleSelected(mod.id, !selected)
                                  }}
                                >
                                  <i className={`${mod.icon} me-1 text-primary`} />
                                  {t(mod.label)}
                                </span>
                                {selected && (
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 text-decoration-none"
                                    onClick={e => toggleModuleExpand(mod.id, e)}
                                    aria-expanded={isExpanded}
                                  >
                                    <i
                                      className={`bx ${
                                        isExpanded ? "bx-chevron-up" : "bx-chevron-down"
                                      }`}
                                    />
                                  </button>
                                )}
                              </div>
                              <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                                {t(mod.description)}
                              </div>
                              {selected && (
                                <div className="mt-2">
                                  <NativeCheckbox
                                    id={`mod_default_${mod.id}`}
                                    checked={isDefault}
                                    className="d-inline-flex align-items-center gap-2 text-success"
                                    inputStyle={{ marginTop: 0 }}
                                    onActivate={() => {
                                      if (!selected) {
                                        setModuleSelected(mod.id, true)
                                      }
                                      setModuleDefaultEnabled(mod.id, !isDefault)
                                    }}
                                  >
                                    <span style={{ fontSize: "0.8rem" }}>
                                      {t("Enabled by default")}
                                    </span>
                                  </NativeCheckbox>
                                </div>
                              )}
                              {selected && isExpanded && moduleCategoryIds.length > 0 && (
                                <div
                                  className="mt-3 pt-2 border-top"
                                  onClick={e => e.stopPropagation()}
                                >
                                  {moduleCategoryIds.map(categoryId => (
                                    <ModuleCategoryPermissions
                                      key={`${mod.id}_${categoryId}`}
                                      categoryId={categoryId}
                                      selection={permissionSelections[Number(categoryId)]}
                                      uiLang={uiLang}
                                      t={t}
                                      onPermissionChange={(permissionId, subPermissionId) =>
                                        handlePlanPermissionChange(
                                          categoryId,
                                          permissionId,
                                          subPermissionId
                                        )
                                      }
                                      onToggleCategoryAll={selectAll =>
                                        handlePlanCategorySelectAll(categoryId, selectAll)
                                      }
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                            {selected && (
                              <Badge
                                color={isDefault ? "success" : "light"}
                                className="flex-shrink-0"
                                style={{ fontSize: "0.7rem" }}
                              >
                                {isDefault ? t("Default") : t("Optional")}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </Col>
                    )
                  })}
                </Row>
                <div className="mt-3 text-muted" style={{ fontSize: "0.85rem" }}>
                  {validSelectedModules.length} {t("module(s) selected")} &middot;{" "}
                  {validSelectedModules.filter(m => m.isDefault).length}{" "}
                  {t("enabled by default")}
                </div>
                {modulesError ? (
                  <div className="text-danger small mt-2">{modulesError}</div>
                ) : null}
              </CardBody>
            </Card>

            {/* ── Plan integrations (catalog tree) ───────────────────────────── */}
            <Card>
              <CardBody>
                <h5 className="card-title mb-1">{t("Integrations")}</h5>
                <p className="text-muted small mb-3">{t("Plan integrations hint")}</p>

                {integrationCatalogTreeLoading ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" size="sm" className="me-2" />
                    {t("Loading integration catalog…")}
                  </div>
                ) : integrationCatalogParents.length === 0 ? (
                  <p className="text-muted small mb-0">
                    {t("No integration groups in catalog.")}
                  </p>
                ) : (
                  <>
                    {integrationCatalogParents.map((category, catIdx) => {
                      const catId = String(category._id || catIdx)
                      const open = expandedParentKeys.includes(catId)
                      const integrations = Array.isArray(category.integrations)
                        ? category.integrations
                        : []
                      const intIds = integrations.map(i => String(i._id)).filter(Boolean)
                      const selectedCount = intIds.filter(id =>
                        selectedIntegrationIds.includes(id)
                      ).length
                      const allOn = intIds.length > 0 && selectedCount === intIds.length

                      const catName =
                        (uiLang === "ar" || uiLang === "sa"
                          ? category.title?.ar
                          : category.title?.en) ||
                        category.title?.en ||
                        category.code ||
                        catId

                      return (
                        <div key={catId} className="border rounded mb-2">

                          {/* accordion header */}
                          <button
                            type="button"
                            style={{ cursor: "pointer", background: "none", border: "none", width: "100%", textAlign: "left", padding: "10px 14px" }}
                            className="d-flex align-items-center justify-content-between bg-light rounded-top"
                            onClick={() =>
                              setExpandedParentKeys(prev =>
                                prev.includes(catId)
                                  ? prev.filter(k => k !== catId)
                                  : [...prev, catId]
                              )
                            }
                          >
                            <span className="d-flex align-items-center gap-2 fw-medium">
                              <i className={`bx ${open ? "bx-chevron-down" : "bx-chevron-right"} text-muted`} />
                              {catName}
                            </span>
                            <Badge color={"light"} className="text-dark border">
                              {selectedCount}/{intIds.length}
                            </Badge>
                          </button>

                          {/* accordion body */}
                          {open && (
                            <div className="p-3 border-top">
                              {integrations.length === 0 ? (
                                <p className="text-muted small mb-0">
                                  {t("No sub-integrations in this group.")}
                                </p>
                              ) : (
                                <>
                                  {/* select-all row */}
                                  <div
                                    className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      setSelectedIntegrationIds(prev => {
                                        if (allOn) return prev.filter(x => !intIds.includes(x))
                                        return [...new Set([...prev, ...intIds])]
                                      })
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      readOnly
                                      checked={allOn}
                                      style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--bs-primary, #556ee6)", flexShrink: 0 }}
                                    />
                                    <span className="small">{t("Select all in this category")}</span>
                                  </div>

                                  {/* integration tiles */}
                                  <Row className="g-2">
                                    {integrations.map(integration => {
                                      const intId = String(integration._id || "")
                                      if (!intId) return null
                                      const isSelected = selectedIntegrationIds.includes(intId)
                                      const intName =
                                        (uiLang === "ar" || uiLang === "sa"
                                          ? integration.title?.ar
                                          : integration.title?.en) ||
                                        integration.title?.en ||
                                        integration.code ||
                                        intId

                                      return (
                                        <Col key={intId} md={6} lg={4}>
                                          <div
                                            className={`border rounded p-2 d-flex align-items-center gap-2 ${isSelected ? "border-primary bg-light" : ""}`}
                                            style={{ cursor: "pointer", minHeight: 44 }}
                                            onClick={() =>
                                              setSelectedIntegrationIds(prev =>
                                                prev.includes(intId)
                                                  ? prev.filter(x => x !== intId)
                                                  : [...prev, intId]
                                              )
                                            }
                                          >
                                            <input
                                              type="checkbox"
                                              readOnly
                                              checked={isSelected}
                                              style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--bs-primary, #556ee6)", flexShrink: 0 }}
                                            />
                                            <span className="small fw-medium">{intName}</span>
                                          </div>
                                        </Col>
                                      )
                                    })}
                                  </Row>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}

                    <div className="mt-2 text-muted small">
                      {selectedIntegrationIds.length}{" "}
                      {t("integration(s) selected for this plan")}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h5 className="card-title mb-1">{t("Social Login")}</h5>
                <p className="text-muted small mb-3">
                  {t(
                    "Allow tenants on this plan to offer Google and/or Apple sign-in to customers. Tenants must also configure credentials before login buttons appear in the app."
                  )}
                </p>
                <Row className="g-2">
                  {[
                    { key: "google", label: t("Google Sign-In") },
                    { key: "apple", label: t("Apple Sign-In") },
                  ].map(({ key, label }) => {
                    const isSelected = socialLogins[key] === true
                    return (
                      <Col key={key} md={6} lg={4}>
                        <div
                          className={`border rounded p-2 d-flex align-items-center gap-2 ${isSelected ? "border-primary bg-light" : ""}`}
                          style={{ cursor: "pointer", minHeight: 44 }}
                          onClick={() =>
                            setSocialLogins(prev => ({
                              ...prev,
                              [key]: !prev[key],
                            }))
                          }
                        >
                          <input
                            type="checkbox"
                            readOnly
                            checked={isSelected}
                            style={{
                              width: 16,
                              height: 16,
                              cursor: "pointer",
                              accentColor: "var(--bs-primary, #556ee6)",
                              flexShrink: 0,
                            }}
                          />
                          <span className="small fw-medium">{label}</span>
                        </div>
                      </Col>
                    )
                  })}
                </Row>
              </CardBody>
            </Card>

            {/* Add-ons & Module Limits — not wired to plan API yet; hidden until enabled.
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="card-title mb-0">{t("Add-ons & Pricing")}</h5>
                    <p className="text-muted small mb-0">
                      {t("Define purchasable add-ons with individual pricing (e.g. extra locations, extra staff accounts).")}
                    </p>
                  </div>
                  <Button color="outline-primary" size="sm" type="button" onClick={addAddOnRow}>
                    <i className="bx bx-plus me-1" />
                    {t("Add Row")}
                  </Button>
                </div>

                {addOns.map((addon, idx) => (
                  <Row key={idx} className="g-2 mb-2 align-items-end">
                    <Col md={3}>
                      <Label className="small">{t("Label")}</Label>
                      <Input
                        placeholder={t("e.g. Extra Locations")}
                        value={addon.label}
                        onChange={e => updateAddOn(idx, "label", e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Label className="small">{t("Description")}</Label>
                      <Input
                        placeholder={t("Short description")}
                        value={addon.description}
                        onChange={e => updateAddOn(idx, "description", e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Label className="small">{t("Price")}</Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="0.00"
                        value={addon.price}
                        onChange={e => updateAddOn(idx, "price", e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Label className="small">{t("Unit (e.g. /location/month)")}</Label>
                      <Input
                        placeholder={t("/location/month")}
                        value={addon.unit}
                        onChange={e => updateAddOn(idx, "unit", e.target.value)}
                      />
                    </Col>
                    <Col md={1}>
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        type="button"
                        onClick={() => removeAddOnRow(idx)}
                        disabled={addOns.length === 1}
                      >
                        <i className="bx bx-trash" />
                      </Button>
                    </Col>
                  </Row>
                ))}
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="card-title mb-0">{t("Module Limits")}</h5>
                    <p className="text-muted small mb-0">
                      {t("Set maximum usage limits per module (e.g. max 5 locations, max 100 items).")}
                    </p>
                  </div>
                  <Button color="outline-primary" size="sm" type="button" onClick={addLimitRow}>
                    <i className="bx bx-plus me-1" />
                    {t("Add Limit")}
                  </Button>
                </div>

                {limits.map((lim, idx) => (
                  <Row key={idx} className="g-2 mb-2 align-items-end">
                    <Col md={4}>
                      <Label className="small">{t("Module")}</Label>
                      <Input
                        type="select"
                        value={lim.moduleId}
                        onChange={e => updateLimit(idx, "moduleId", e.target.value)}
                      >
                        <option value="">{t("Select module")}</option>
                        {SYSTEM_MODULES.map(mod => (
                          <option key={mod.id} value={mod.id}>{t(mod.label)}</option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={4}>
                      <Label className="small">{t("Limit Key (e.g. maxLocations)")}</Label>
                      <Input
                        placeholder={t("e.g. maxLocations")}
                        value={lim.limitKey}
                        onChange={e => updateLimit(idx, "limitKey", e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Label className="small">{t("Max Value")}</Label>
                      <Input
                        type="number"
                        min={0}
                        placeholder="0"
                        value={lim.limitValue}
                        onChange={e => updateLimit(idx, "limitValue", e.target.value)}
                      />
                    </Col>
                    <Col md={1}>
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        type="button"
                        onClick={() => removeLimitRow(idx)}
                        disabled={limits.length === 1}
                      >
                        <i className="bx bx-trash" />
                      </Button>
                    </Col>
                  </Row>
                ))}
              </CardBody>
            </Card>
            */}

            {/* ── Submit ────────────────────────────────────────────────────── */}
            {!isView && <div className="d-flex gap-2 mb-4">
              <Button
                color="primary"
                type="submit"
                form="sa-plan-form"
                disabled={planActionLoading}
              >
                {planActionLoading ? (
                  <Spinner size="sm" />
                ) : isEdit ? (
                  t("Save Changes")
                ) : (
                  t("Create Plan")
                )}
              </Button>
              <Button
                color="secondary"
                outline
                type="button"
                onClick={() => navigate(ROUTES.SA_PLANS)}
              >
                {t("Cancel")}
              </Button>
            </div>}
          </div>
          {isView && (
            <div className="d-flex gap-2 mb-4">
              <Button
                color="secondary"
                outline
                type="button"
                onClick={() => navigate(ROUTES.SA_PLANS)}
              >
                {t("Back to Plans")}
              </Button>
            </div>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default AddEditPlan
